import React, { useEffect, Fragment, useCallback, useState } from 'react';
import { isEmpty, debounce } from 'lodash';
import {
  INVALID_POSTAL_CODE_CHARS,
  MINIMUM_POSTAL_CODE_LENGTH,
} from '../../../constants';
import request from '../../../request';
import Countries from './Countries';
import DoNotSeeMyLocationAnswer from './DoNotSeeMyLocationAnswer';
import PreferNotToAnswer from './PreferNotToAnswer';
import SelectionHint from './SelectionHint';
import useComponentWillMount from '../../hooks/useComponentWillMount';

export default function PostalSearch() {
  const { getText } = Pollfish.ui;
  const [countries, setCountries] = useState([]);
  const [lastCleanInputValue, setLastCleanInputValue] = useState('');
  const [allowNotMyLocation, setAllowNotMyLocation] = useState(false);

  const setLoaderVisibility = useCallback((isLoading) => {
    $('.answers-wrapper .form-container').toggleClass('loading', isLoading);
    $('#submit-btn').toggleClass('disabled', isLoading);
  }, []);

  const fetchRemoteData = useCallback(
    debounce((postalCodeQuery) => {
      if (
        postalCodeQuery &&
        postalCodeQuery.length >= MINIMUM_POSTAL_CODE_LENGTH
      ) {
        // fetch the remote data here
        setLoaderVisibility(true);
        request({
          url: `/v2/device/autocomplete/postal-code?search=${encodeURIComponent(
            postalCodeQuery,
          )}`,
        })
          .then((foundCountries) => {
            setCountries(foundCountries);
          })
          .finally(() => {
            setLoaderVisibility(false);
            setAllowNotMyLocation(true);
          });
      } else {
        setCountries([]);
        setLoaderVisibility(false);
        setAllowNotMyLocation(false);
      }
    }, 250),
    [setLoaderVisibility, setCountries],
  );

  const handleInput = useCallback(
    (event) => {
      const { target } = event;
      const { value = '' } = target;
      const cleanedValue = $.trim(value).replace(INVALID_POSTAL_CODE_CHARS, '');
      target.value = cleanedValue;

      if (cleanedValue !== lastCleanInputValue) {
        setLastCleanInputValue(cleanedValue);
        fetchRemoteData(cleanedValue);
      }
    },
    [lastCleanInputValue, fetchRemoteData],
  );

  const handleCollapse = useCallback((event) => {
    const { target } = event;
    const country = $(target).closest('.country');
    const withSelection = country.hasClass('with-selection');

    if (withSelection) return;

    const alreadyOpen = country
      .toggleClass('country-open')
      .siblings('.country-open')
      .removeClass('country-open');
    alreadyOpen.find('.country-codes').slideUp();
    if (country.hasClass('country-open')) {
      country.find('.country-codes').slideDown(200, () => {
        const countryDomNode = country.get(0);
        if (countryDomNode.scrollIntoView) {
          countryDomNode.scrollIntoView();
        }
      });
    } else {
      country.find('.country-codes').slideUp();
    }
  }, []);

  const handleTap = useCallback((event) => {
    event.preventDefault();
    const { currentTarget } = event;
    const $target = $(currentTarget);
    let selectedPostalCodeOption = '';

    if ($target.is('.selected')) {
      selectedPostalCodeOption = '';
      $target.removeClass('selected');
      $target.parents('.countries,.country').removeClass('with-selection');
      if (currentTarget.scrollIntoView) {
        currentTarget.scrollIntoView();
      }
    } else {
      selectedPostalCodeOption = $target.attr('data-postal-code-id');
      $('.country-code.selected').removeClass('selected');
      $target.addClass('selected');
      $target.parents('.countries,.country').addClass('with-selection');
    }
    $('#postalSearch').val(selectedPostalCodeOption);
  }, []);

  useComponentWillMount(() => {
    $('#wrapper').on(
      'tap',
      '.country-open .country-code, .ignore-collapse .country-code',
      handleTap,
    );
    $('#wrapper').on('tap', '.country-header', handleCollapse);
  });

  useEffect(
    () => () => {
      $('#wrapper').off(
        'tap',
        '.country-open .country-code, .ignore-collapse .country-code',
      );
      $('#wrapper').off('tap', '.country-header');
    },
    [],
  );

  return (
    <Fragment>
      <div className="form-container with-loading-indicator">
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={Pollfish.helper.shouldAutofocus()}
          id="postalSearch"
          className="input-text"
          type="text"
          onChange={handleInput}
          placeholder={getText('freeTextBox')}
        />
      </div>
      <div className="postal-code-answers">
        <SelectionHint />
        <ul className="countries">
          <PreferNotToAnswer />
          {!isEmpty(countries) && (
            <Countries query={lastCleanInputValue} countries={countries} />
          )}
          {allowNotMyLocation && (
            <DoNotSeeMyLocationAnswer query={lastCleanInputValue} />
          )}
        </ul>
      </div>
    </Fragment>
  );
}
